import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../api/auth';
import type { LoginResponse } from '../types/auth';
import { apiService } from '../utils/apiService';
import { ErrorHandler } from '../utils/errorHandler';
import { canAccessAdminDashboard, normalizeUserRoles } from '../utils/permissions';
import { showToast } from '../utils/toast';

interface AuthState {
    user: LoginResponse | null;
    isLoading: boolean;
    error: string | null;
    isAuthenticated: boolean;
    isGuest: boolean;
    bootstrapped: boolean;
    login: (phone: string, password: string) => Promise<boolean>;
    bootstrapFromStoredToken: () => Promise<void>;
    loginAsGuest: () => void;
    register: (firstname: string, lastname: string, phone: string, password: string, teamId?: number) => Promise<boolean>;
    logout: () => void;
}

const ADMIN_ACCESS_ERROR = 'У этого аккаунта нет доступа к админ-панели.';

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => {
            // Проверяем при инициализации, если это гостевая сессия
            const isGuestSession = typeof window !== 'undefined' && localStorage.getItem('is_guest') === 'true';
            
            return {
                user: null,
                isLoading: false,
                error: null,
                isAuthenticated: false, // Даже если гость, для страницы логина мы не "авторизованы"
                isGuest: isGuestSession,
                bootstrapped: false,

                login: async (phone, password) => {
                    set({ isLoading: true, error: null });
                    
                    try {
                        const response = await apiService.execute(
                            () => authApi.login({ phone, password }),
                            'login'
                        );
                        const userRoles = normalizeUserRoles(response);
                        
                        set({
                            user: response,
                            isAuthenticated: true,
                            isGuest: false,
                            isLoading: false
                        });
                        
                        localStorage.setItem('auth_token', response.token);
                        showToast('Login successful!', 'success');
                        return true;
                    } 
                    catch (error: any) {
                        const errorMessage = ErrorHandler.handle(error);
                        set({
                            error: errorMessage.message,
                            isLoading: false
                        });
                        
                        return false;
                    }
                },
                bootstrapFromStoredToken: async () => {
                    const { bootstrapped, isLoading } = useAuthStore.getState();
                    if (bootstrapped || isLoading) {
                        return;
                    }

                    const dashboardToken = localStorage.getItem('auth_token');
                    const mobileToken = localStorage.getItem('sport_mobile_access_token');
                    const activeToken = dashboardToken || mobileToken;

                    if (!activeToken) {
                        set({ bootstrapped: true });
                        return;
                    }

                    set({ isLoading: true, error: null });

                    const restoreDashboardToken = dashboardToken ?? null;

                    try {
                        // Reuse mobile session token for dashboard auto-login.
                        localStorage.setItem('auth_token', activeToken);

                        const currentUser = await authApi.getMe();
                        const hydratedUser: LoginResponse = {
                            token: activeToken,
                            firstname: currentUser.firstname || '',
                            lastname: currentUser.lastname || '',
                            phone: currentUser.phone || '',
                            role: currentUser.role ?? currentUser.roles ?? [],
                            roles: (currentUser.roles ?? []).map((name, index) => ({ id: index + 1, name })),
                            profilePictureUrl: currentUser.profilePictureUrl ?? null,
                        };
                        const userRoles = normalizeUserRoles(hydratedUser);

                        if (!canAccessAdminDashboard(userRoles)) {
                            // Keep dashboard auth strict: only admin-like roles are auto-authorized here.
                            if (restoreDashboardToken) {
                                localStorage.setItem('auth_token', restoreDashboardToken);
                            } else {
                                localStorage.removeItem('auth_token');
                            }
                            set({
                                user: null,
                                isAuthenticated: false,
                                isGuest: false,
                                isLoading: false,
                                bootstrapped: true,
                            });
                            return;
                        }

                        set({
                            user: hydratedUser,
                            isAuthenticated: true,
                            isGuest: false,
                            isLoading: false,
                            bootstrapped: true,
                        });
                    } catch {
                        if (restoreDashboardToken) {
                            localStorage.setItem('auth_token', restoreDashboardToken);
                        } else {
                            localStorage.removeItem('auth_token');
                        }
                        set({
                            user: null,
                            isAuthenticated: false,
                            isGuest: false,
                            isLoading: false,
                            bootstrapped: true,
                        });
                    }
                },

                loginAsGuest: () => {
                    const guestUser: LoginResponse = {
                        token: 'guest-token-' + Date.now(),
                        firstname: 'Guest',
                        lastname: 'User',
                        phone: '+7 (777) 000 00 00',
                        profilePictureUrl: null,
                        role: ['GUEST']
                    };

                    set({
                        user: guestUser,
                        isAuthenticated: true,
                        isGuest: true,
                        isLoading: false,
                        error: null,
                        bootstrapped: true,
                    });

                    localStorage.setItem('auth_token', guestUser.token);
                    localStorage.setItem('is_guest', 'true');
                    showToast('Вы вошли как гость. Доступен только просмотр данных.', 'info');
                },

                register: async (firstname, lastname, phone, password, teamId) => {
                    set({ isLoading: true, error: null });
                    
                    try {
                        await apiService.execute(
                            () => authApi.register({ firstname, lastname, phone, password, teamId }),
                            'register'
                        );
                        set({ isLoading: false });
                        
                        showToast('Registration successful! Please login.', 'success');
                        return true;
                    } 
                    catch (error: any) {
                        const errorMessage = ErrorHandler.handle(error);
                        set({
                            error: errorMessage.message,
                            isLoading: false
                        });
                        
                        return false;
                    }
                },

                logout: () => {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('is_guest');
                    apiService.clearCache(); // Clear API cache on logout
                    set({ user: null, isAuthenticated: false, isGuest: false, bootstrapped: true });
                    showToast('Logged out successfully', 'info');
                    // Note: Navigation should be handled by React Router in the component
                }
            };
        },
        {
            name: 'auth-storage',
        }
    )
);
